import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { FilePond, registerPlugin } from 'react-filepond'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import { Label } from '../../components'
import { getAttachmentName } from '../../utils/attachmentName'
import getWorkspaceURL from '../../config/workspaceURL'
// Import FilePond styles
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

registerPlugin(FilePondPluginFileValidateType, FilePondPluginImagePreview)

const mime = require('mime-types')

const FilesInProcess = (initValues = []) => {
  let files = initValues

  const addFile = (file) => {
    files = [...files, file]
  }
  const clear = () => {
    files = []
  }
  const checkStatus = () => {
    return files.filter(file => !file.isUploading).length === files.length
  }
  const updateFile = (name, values) => {
    files = files.map(file => {
      if (file.name === name) {
        return ({ ...file, ...values })
      }
      return file
    })
  }
  const getFiles = () => files
  return [addFile, checkStatus, clear, updateFile, getFiles]
}

const File = ({ id, name, label, helpText, index, value, recordId, onChange, isList, accept }) => {

  const params = useParams()
  const [files, setFiles] = useState([])
  const [addFile, checkStatus, clear, updateFile, getFiles] = FilesInProcess([])
  const getFileName = key => {
    const SplitFile = key.split('/')
    return getAttachmentName(SplitFile[SplitFile.length - 1])
  }
  const requestFile = async file => {
    if (file) {
      const { data } = await axios.get(file.key.replace(/^.+?\//g, ''), { responseType: 'blob' })
      data.name = getFileName(file.key)
      return ({
        source: data,
        options: {
          type: 'limbo',
        },
      })
    } else {
      return null
    }
  }

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        if (Array.isArray(value)) {
          const nextFiles = await Promise.all(value.map(async value => {
            const FileName = getFileName(value.key)
            const hasInPreFiles = files.find(_file => _file.source.name === FileName)
            if (hasInPreFiles) {
              return hasInPreFiles
            } else {
              return await requestFile(value)
            }
          }))
          setFiles(nextFiles)
        } else {
          if (files[0]) {
            if (value) {
              if (files[0].source.name === getFileName(value.key)) {
                return null
              }
            } else {
              setFiles([])
            }
          } else {
            setFiles(await Promise.all([value].map(requestFile)))
          }

        }
      } catch (error) {
        console.log(error)
      }
    }
    if (value.length > 0) {
      fetchFiles()
    } else if (!!value) {
      fetchFiles()
    } else {
      setFiles([])
    }

  }, [value])
  const process = (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()
    const formData = new FormData()
    formData.append('file', file)
    addFile({
      file,
      name: file.name,
      key: '',
      isUploading: true,
    })
    const config = {
      baseURL: getWorkspaceURL(),
      onUploadProgress: (progressEvent) => {
        const totalLength = progressEvent.lengthComputable
          ? progressEvent.total
          : progressEvent.target.getResponseHeader('content-length') ||
          progressEvent.target.getResponseHeader('x-decompressed-content-length')
        if (totalLength !== null) {
          progress(progressEvent.lengthComputable, progressEvent.loaded, totalLength)
        }
      },
      cancelToken: source.token,
    }
    if (!recordId) {
      throw new Error('Undefined record ID')
    }
    axios.post(`/upload/files/${params.adapter}/${recordId}/${name}`, formData, config)
      .then((response) => {
        load({
          _contentType: response.data.type || file.type,
          key: response.data.url,
        })
        if (isList) {
          updateFile(file.name, {
            key: response.data.url,
            type: response.data.type,
            isUploading: false,
          })
          if (checkStatus()) {
            const nextFiles = getFiles()
            setFiles([
              ...files, ...nextFiles.map(_file => ({
                source: _file.file,
                options: {
                  type: 'limbo',
                },
              }))])
            const nextArray = [
              ...value, ...nextFiles.map(_file => ({
                _contentType: _file.type || _file.file.type,
                key: _file.key,
              }))]
            onChange(nextArray)
            clear()
          }
        } else {
          setFiles([
            ({
              source: file,
              options: {
                type: 'limbo',
              },
            })])
          onChange({
            _contentType: response.data.type || file.type,
            key: response.data.url,
          })
        }

      })
      .catch(thrown => {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message)
        } else {
          error(thrown)
        }
      })
    return {
      abort: () => {
        source.cancel('Operation canceled by the user.')
        abort()
      },
    }
  }

  const onRemoveFile = (error, file) => {
    if (error) {
      console.log(error)
    }
    if (isList) {
      const nextValue = value.filter(f => !f.key.endsWith(file.filename))
      const nextFiles = files.filter(_file => _file.source.name !== file.filename)
      setFiles(nextFiles)
      onChange(nextValue)
    } else {
      setFiles([])
      onChange(null)
    }
  }

  const MemoFilePond = useMemo(() => {
    return (<FilePond
      allowMultiple={isList}
      name={name}
      files={files}
      onremovefile={onRemoveFile}
      server={{
        process,
      }}
      allowFileTypeValidation={Boolean(accept)}
      acceptedFileTypes={accept && accept.split(',').map(s => s.trim())}
      fileValidateTypeLabelExpectedTypesMap={{
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      }}
      fileValidateTypeDetectType={async (source, type) => mime.lookup(source.name)}
    />)
  }, [files, isList, recordId, accept])

  return (
    <Fragment>
      <Label name={name} label={label} helpText={helpText} forId={id}/>
      {MemoFilePond}
    </Fragment>
  )
}

export default File
